/**
 * Opalca — Main JavaScript
 * Scroll animations, mobile nav, FAQ accordion, smooth interactions
 * Shopify-compatible: no framework dependencies, vanilla JS only
 */

document.addEventListener('DOMContentLoaded', () => {
  initScrollReveal();
  initStaggerReveal();
  initCounterAnimation();
  initMobileNav();
  initFAQ();
  initSmoothScroll();
  initHeaderScroll();
  initPricingToggle();
  initSellingPlanToggle();
  initDirectCheckout();
  initIconHoverSwap();
});

/* ============================================
   ICON HOVER SWAP — swap default ↔ yellow-bg icon on hover
   ============================================ */
function initIconHoverSwap() {
  const icons = document.querySelectorAll('img[data-hover-icon]');
  icons.forEach(function (img) {
    const hoverSrc = img.getAttribute('data-hover-icon');
    if (!hoverSrc) return;
    const defaultSrc = img.getAttribute('src');
    // Preload hover image to avoid flicker
    const preload = new Image();
    preload.src = hoverSrc;
    // Find the closest interactive ancestor (link, card, etc.)
    const target = img.closest('a, .product-card, .connector-group-card, .header__dropdown-item, [data-icon-hover-target]') || img.parentElement;
    if (!target) return;
    target.addEventListener('mouseenter', function () { img.src = hoverSrc; });
    target.addEventListener('mouseleave', function () { img.src = defaultSrc; });
    target.addEventListener('focusin', function () { img.src = hoverSrc; });
    target.addEventListener('focusout', function () { img.src = defaultSrc; });
  });
}

/* ============================================
   SCROLL REVEAL ANIMATIONS
   ============================================ */
function initScrollReveal() {
  const reveals = document.querySelectorAll('.reveal');
  if (!reveals.length) return;

  const markInView = () => {
    reveals.forEach(el => {
      const rect = el.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) {
        el.classList.add('visible');
      }
    });
  };

  markInView();
  window.addEventListener('hashchange', () => setTimeout(markInView, 0));
  window.addEventListener('resize', markInView, { passive: true });
  window.addEventListener('load', () => {
    markInView();
    requestAnimationFrame(markInView);
    setTimeout(markInView, 200);
    setTimeout(markInView, 900);
  });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });

  reveals.forEach(el => observer.observe(el));
}

/* ============================================
   STAGGER REVEAL (for grids)
   ============================================ */
function initStaggerReveal() {
  const staggers = document.querySelectorAll('.reveal-stagger');
  if (!staggers.length) return;

  const markInView = () => {
    staggers.forEach(el => {
      const rect = el.getBoundingClientRect();
      const inView = rect.top < window.innerHeight && rect.bottom > 0;
      if (inView) {
        el.classList.add('visible');
      }
    });
  };

  markInView();
  window.addEventListener('hashchange', () => setTimeout(markInView, 0));
  window.addEventListener('resize', markInView, { passive: true });
  window.addEventListener('load', () => {
    markInView();
    requestAnimationFrame(markInView);
    setTimeout(markInView, 200);
    setTimeout(markInView, 900);
  });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -30px 0px'
  });

  staggers.forEach(el => observer.observe(el));
}

/* ============================================
   COUNTER ANIMATION (for stats numbers)
   ============================================ */
function initCounterAnimation() {
  const counters = document.querySelectorAll('.stats-grid__item-number');
  if (!counters.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        animateCounter(entry.target);
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.5 });

  counters.forEach(el => observer.observe(el));
}

function animateCounter(el) {
  const text = el.textContent.trim();
  const match = text.match(/^([\d,.]+)(.*)$/);
  if (!match) return;

  const numStr = match[1].replace(/,/g, '');
  const suffix = match[2] || '';
  const target = parseFloat(numStr);
  if (isNaN(target)) return;

  const isDecimal = numStr.includes('.');
  const duration = 1200;
  const start = performance.now();

  function step(now) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const current = target * eased;

    if (isDecimal) {
      el.textContent = current.toFixed(1) + suffix;
    } else {
      el.textContent = Math.floor(current).toLocaleString('it-IT') + suffix;
    }

    if (progress < 1) {
      requestAnimationFrame(step);
    } else {
      el.textContent = text;
    }
  }

  requestAnimationFrame(step);
}

/* ============================================
   MOBILE NAVIGATION
   ============================================ */
function initMobileNav() {
  const toggle = document.getElementById('mobileToggle');
  const nav = document.getElementById('mobileNav');
  if (!toggle || !nav) return;

  const setMenuState = (isOpen) => {
    nav.classList.toggle('open', isOpen);
    toggle.classList.toggle('active', isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
    nav.setAttribute('aria-hidden', String(!isOpen));
    document.body.style.overflow = isOpen ? 'hidden' : '';
  };

  setMenuState(false);

  toggle.addEventListener('click', () => {
    setMenuState(!nav.classList.contains('open'));
  });

  nav.querySelectorAll('.mobile-nav__link, .mobile-nav__sublink').forEach(link => {
    link.addEventListener('click', () => {
      setMenuState(false);
    });
  });

  // Mobile Product submenu toggle
  const productToggle = document.getElementById('mobileProductToggle');
  const productSubmenu = document.getElementById('mobileProductSubmenu');
  if (productToggle && productSubmenu) {
    productToggle.addEventListener('click', () => {
      const isOpen = productSubmenu.classList.contains('open');
      productSubmenu.classList.toggle('open', !isOpen);
      productToggle.classList.toggle('open', !isOpen);
    });
  }
}

/* ============================================
   FAQ ACCORDION
   ============================================ */
function initFAQ() {
  const items = document.querySelectorAll('.faq__item');
  if (!items.length) return;

  items.forEach(item => {
    const question = item.querySelector('.faq__question');
    if (!question) return;

    question.addEventListener('click', () => {
      const isActive = item.classList.contains('active');

      items.forEach(i => i.classList.remove('active'));

      if (!isActive) {
        item.classList.add('active');
      }
    });
  });
}

/* ============================================
   SMOOTH SCROLL
   ============================================ */
function initSmoothScroll() {
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();
      const headerEl = document.querySelector('.header');
      const headerHeight = headerEl instanceof HTMLElement ? headerEl.offsetHeight : 72;
      const top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    });
  });
}

/* ============================================
   HEADER SCROLL EFFECT
   ============================================ */
function initHeaderScroll() {
  const header = document.getElementById('header');
  if (!header) return;

  window.addEventListener('scroll', () => {
    const currentScroll = window.pageYOffset;

    if (currentScroll > 50) {
      header.classList.add('scrolled');
    } else {
      header.classList.remove('scrolled');
    }
  }, { passive: true });
}

/* ============================================
   PRICING TOGGLE (monthly/annual)
   ============================================ */
function initPricingToggle() {
  const toggle = document.querySelector('.pricing__toggle-switch');
  if (!toggle) return;

  const monthlyLabel = document.querySelector('.pricing__toggle-label:first-child');
  const annualLabel = document.querySelector('.pricing__toggle-label:last-child');
  /** @type {NodeListOf<HTMLElement>} */
  const amounts = document.querySelectorAll('.pricing-card__amount');
  /** @type {NodeListOf<HTMLElement>} */
  const periods = document.querySelectorAll('.pricing-card__period');

  let isAnnual = false;

  const monthlyPrices = [];
  const annualPrices = [];

  amounts.forEach(el => {
    const monthly = el.dataset.monthly;
    const annual = el.dataset.annual;
    monthlyPrices.push(monthly);
    annualPrices.push(annual);
  });

  const billingNotes = document.querySelectorAll('[data-annual-note]');

  toggle.addEventListener('click', () => {
    isAnnual = !isAnnual;
    toggle.classList.toggle('active', isAnnual);

    if (monthlyLabel) monthlyLabel.classList.toggle('active', !isAnnual);
    if (annualLabel) annualLabel.classList.toggle('active', isAnnual);

    amounts.forEach((el, i) => {
      el.textContent = isAnnual ? annualPrices[i] : monthlyPrices[i];
    });

    periods.forEach(el => {
      el.textContent = isAnnual ? '/mese' : '/mese';
    });

    billingNotes.forEach(note => {
      note.style.display = isAnnual ? 'block' : 'none';
    });
  });
}

/* ============================================
   SELLING PLAN TOGGLE (Appstle subscription monthly/annual)
   ============================================ */
function initSellingPlanToggle() {
  var toggle = document.querySelector('.pricing__toggle-switch');
  if (!toggle) return;
  var planInputs = document.querySelectorAll('.js-selling-plan');
  if (!planInputs.length) return;

  function updatePlans() {
    var isAnnual = toggle.classList.contains('active');
    planInputs.forEach(function(input) {
      var plans = [];
      try { plans = JSON.parse(input.dataset.plans || '[]'); } catch (e) { return; }
      var target = plans.find(function(p) {
        var name = (p.name || '').toLowerCase();
        return isAnnual
          ? (name.indexOf('annual') !== -1 || name.indexOf('annuale') !== -1 || name.indexOf('year') !== -1 || name.indexOf('anno') !== -1)
          : (name.indexOf('month') !== -1 || name.indexOf('mensile') !== -1 || name.indexOf('mese') !== -1);
      });
      if (target) input.value = target.id;
    });
  }

  toggle.addEventListener('click', function() {
    requestAnimationFrame(updatePlans);
  });
  updatePlans();
}

/* ============================================
   DIRECT TO CHECKOUT (skip cart page)
   ============================================ */
function initDirectCheckout() {
  document.querySelectorAll('.pricing-card__form').forEach(function(form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var formData = new FormData(form);
      var btn = form.querySelector('button[type="submit"]');
      if (btn) {
        btn.disabled = true;
        btn.textContent = 'Caricamento...';
      }

      fetch('/cart/clear.js', { method: 'POST' })
        .then(function() {
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: parseInt(formData.get('id'), 10),
              quantity: 1,
              selling_plan: parseInt(formData.get('selling_plan'), 10)
            })
          });
        })
        .then(function(resp) {
          if (!resp.ok) throw new Error('Cart error');
          window.location.href = '/checkout';
        })
        .catch(function() {
          if (btn) {
            btn.disabled = false;
            btn.textContent = form.dataset.originalLabel || 'Riprova';
          }
        });
    });
  });
}

/* Shopify Theme Editor support */
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', function() {
    initScrollReveal();
    initStaggerReveal();
    initCounterAnimation();
    initMobileNav();
    initFAQ();
    initSmoothScroll();
    initHeaderScroll();
    initPricingToggle();
    initSellingPlanToggle();
    initDirectCheckout();
    initIconHoverSwap();
  });
}
